export const SPECIALTY_TERMS = [
    // ---------------------------------------------------------------- workcomp
    {
        id: 'exclusive-remedy',
        term: 'Exclusive remedy',
        category: 'workcomp',
        essential: true,
        def: 'The bargain at the heart of workers\' comp: the employee gets benefits regardless of fault, and gives up the right to sue the employer for the injury.',
        detail: 'It has holes. Intentional acts, dual-capacity claims, and action-over suits routed through a third party all get round it — which is what employers liability is for.',
        see: ['workers-comp', 'employers-liability', 'action-over'],
    },
    {
        id: 'employers-liability',
        term: 'Employers liability',
        aka: ['part two', 'el'],
        category: 'workcomp',
        essential: true,
        def: 'Part Two of a workers\' comp policy. It covers the employee lawsuits that comp benefits do not bar — action-over claims, consequential injury to a family member, dual capacity, and third-party-over suits.',
        detail: 'Written with limits, commonly 100/500/100 or 1,000/1,000/1,000. Umbrellas normally require $1m employers liability underneath, and a policy left at statutory minimums creates a gap in the tower.',
        see: ['workers-comp', 'exclusive-remedy', 'action-over', 'underlying-limits'],
    },
    {
        id: 'other-states-coverage',
        term: 'Other states coverage',
        aka: ['part three', '3a', '3c'],
        category: 'workcomp',
        def: 'Part Three of the comp policy, listing states where the insured has no current operations but might. An injury in a state not listed may not be covered.',
        detail: 'Listing "all states except" the monopolistic ones and the insured\'s own is the safe default. An employee sent to a job in an unlisted state is the classic uncovered comp claim.',
        see: ['workers-comp', 'monopolistic-state', 'extraterritorial'],
    },
    {
        id: 'monopolistic-state',
        term: 'Monopolistic state fund',
        category: 'workcomp',
        def: 'A state where workers\' comp can only be bought from the state itself — North Dakota, Ohio, Washington and Wyoming.',
        detail: 'The state fund provides no employers liability, so a stop-gap endorsement on the CGL or comp policy is needed to fill Part Two in those states.',
        see: ['stop-gap', 'other-states-coverage', 'state-fund'],
    },
    {
        id: 'stop-gap',
        term: 'Stop gap coverage',
        category: 'workcomp',
        def: 'Employers liability coverage added by endorsement for operations in monopolistic states, where the state fund provides none.',
        see: ['monopolistic-state', 'employers-liability'],
    },
    {
        id: 'state-fund',
        term: 'State fund',
        category: 'workcomp',
        def: 'A state-operated workers\' comp insurer. Competitive in some states, the only option in monopolistic ones, and often the market of last resort.',
        see: ['monopolistic-state', 'assigned-risk'],
    },
    {
        id: 'assigned-risk',
        term: 'Assigned risk pool',
        aka: ['residual market'],
        category: 'workcomp',
        def: 'The market of last resort for employers no carrier will voluntarily write. Coverage is guaranteed; the price is not competitive.',
        detail: 'Getting an account out of assigned risk after two clean years is a concrete, provable piece of value an agent delivers.',
        see: ['state-fund', 'experience-mod'],
    },
    {
        id: 'ncci',
        term: 'NCCI',
        category: 'workcomp',
        def: 'The National Council on Compensation Insurance — it sets comp class codes, collects payroll and loss data, and calculates experience modification factors in most states.',
        detail: 'Some large states run their own bureaus instead: California, New York, Pennsylvania, Michigan, and several others. Their rules and mod formulas differ.',
        see: ['class-code', 'experience-mod', 'loss-cost'],
    },
    {
        id: 'experience-mod',
        term: 'Experience modification factor',
        short: 'EMR',
        aka: ['mod', 'xmod', 'e-mod', 'emr'],
        category: 'workcomp',
        essential: true,
        def: 'A multiplier comparing an employer\'s actual losses with what is expected for their size and class. 1.00 is average; below is a credit, above is a debit.',
        detail: 'It uses three years of data, ending one year before the current term, so today\'s mod reflects claims from two to four years ago and today\'s good year cannot help until later. Frequency hurts more than severity: several small claims raise a mod more than one large one, because primary losses count in full and excess losses are heavily discounted.',
        confused: 'A mod is not a discount the carrier chooses. It is a bureau calculation applied before the carrier\'s own credits and debits.',
        see: ['primary-losses', 'split-point', 'elr', 'ncci', 'mod-audit'],
    },
    {
        id: 'primary-losses',
        term: 'Primary losses',
        category: 'workcomp',
        def: 'The first slice of each claim — up to the split point — which counts in full in the experience mod calculation.',
        detail: 'This is why a $12,000 claim and a $250,000 claim can affect a mod almost identically. Managing frequency and closing small medical-only claims is where mod improvement actually comes from.',
        see: ['experience-mod', 'split-point', 'excess-losses'],
    },
    {
        id: 'excess-losses',
        term: 'Excess losses',
        category: 'workcomp',
        def: 'The part of each claim above the split point, which is heavily discounted in the mod calculation.',
        see: ['primary-losses', 'split-point', 'experience-mod'],
    },
    {
        id: 'split-point',
        term: 'Split point',
        category: 'workcomp',
        def: 'The dollar threshold dividing a claim into primary and excess losses for the mod calculation — $18,500 under NCCI as of recent years, indexed periodically.',
        see: ['primary-losses', 'excess-losses', 'experience-mod'],
    },
    {
        id: 'elr',
        term: 'Expected loss rate',
        short: 'ELR',
        category: 'workcomp',
        def: 'The losses expected per $100 of payroll for a class code, used to build the expected losses in the mod formula.',
        see: ['experience-mod', 'd-ratio', 'class-code'],
    },
    {
        id: 'd-ratio',
        term: 'D-ratio',
        category: 'workcomp',
        def: 'The share of a class code\'s expected losses that is expected to be primary. It sets the expected primary losses in the mod calculation.',
        see: ['elr', 'primary-losses', 'experience-mod'],
    },
    {
        id: 'medical-only',
        term: 'Medical only claim',
        category: 'workcomp',
        def: 'A claim with medical treatment but no lost time. In most states only 30% of its value counts in the experience mod.',
        detail: 'The 70% discount is why getting an injured employee back on light duty before the waiting period expires is worth real money on the mod, not just on the claim.',
        see: ['experience-mod', 'return-to-work', 'indemnity-claim'],
    },
    {
        id: 'indemnity-claim',
        term: 'Indemnity claim',
        aka: ['lost time claim'],
        category: 'workcomp',
        def: 'A comp claim involving wage replacement because the employee could not work. It counts at full value in the mod.',
        see: ['medical-only', 'return-to-work', 'experience-mod'],
    },
    {
        id: 'return-to-work',
        term: 'Return to work',
        aka: ['light duty', 'modified duty'],
        category: 'workcomp',
        def: 'Bringing an injured employee back in a limited role while they recover, rather than keeping them off entirely.',
        detail: 'The most effective loss-control measure in comp. It converts indemnity claims into medical-only ones, shortens duration, and it is the concrete thing to help a client build before their next mod calculation.',
        see: ['medical-only', 'indemnity-claim', 'mmi', 'loss-control'],
    },
    {
        id: 'mmi',
        term: 'Maximum medical improvement',
        short: 'MMI',
        category: 'workcomp',
        def: 'The point at which an injured worker\'s condition has stabilised and further treatment will not improve it. Permanent disability is rated from here.',
        see: ['return-to-work', 'permanent-partial'],
    },
    {
        id: 'permanent-partial',
        term: 'Permanent partial disability',
        short: 'PPD',
        category: 'workcomp',
        def: 'A lasting impairment that does not prevent all work. Benefits are paid by a state schedule based on the body part and impairment rating.',
        see: ['mmi', 'indemnity-claim'],
    },
    {
        id: 'mod-audit',
        term: 'Mod review',
        aka: ['mod audit', 'unit stat review'],
        category: 'workcomp',
        def: 'Checking the payroll and claim data reported to the rating bureau for errors before the mod is calculated.',
        detail: 'Errors are common — a claim reported open that closed for less, payroll in the wrong class, a claim that should have been subrogated. A correction can be filed and the mod recalculated, sometimes retroactively.',
        see: ['experience-mod', 'unit-stat', 'reserve'],
    },
    {
        id: 'unit-stat',
        term: 'Unit statistical report',
        aka: ['unit stat', 'usr'],
        category: 'workcomp',
        def: 'The payroll and loss data a carrier reports to the rating bureau, valued 18 months after policy inception and annually after. It feeds the experience mod.',
        detail: 'That 18-month valuation is the deadline that matters: an open reserve reduced afterwards does not help the mod until the next report.',
        see: ['mod-audit', 'experience-mod', 'valuation-date'],
    },
    {
        id: 'ghost-policy',
        term: 'Ghost policy',
        category: 'workcomp',
        def: 'A minimum-premium comp policy for an owner-only business with no employees, bought because a general contractor or a client requires a certificate.',
        detail: 'It excludes the owner, so it covers nobody. It satisfies a contract requirement and nothing else — worth saying out loud to a sole proprietor who thinks they have bought protection for themselves.',
        see: ['officer-exclusion', 'sole-proprietor', 'coi'],
    },
    {
        id: 'officer-exclusion',
        term: 'Officer inclusion / exclusion',
        category: 'workcomp',
        def: 'Whether corporate officers and LLC members are covered by the comp policy. Most states let them elect out, which removes their payroll from the premium.',
        detail: 'Excluded officers have no comp benefits and no employers liability, and their health plan may exclude work injuries. Cheaper is not automatically right here.',
        see: ['ghost-policy', 'sole-proprietor', 'payroll-limitation'],
    },
    {
        id: 'sole-proprietor',
        term: 'Sole proprietor exclusion',
        category: 'workcomp',
        def: 'A sole proprietor or partner is generally not automatically covered by their own comp policy and must elect in.',
        see: ['officer-exclusion', 'ghost-policy'],
    },
    {
        id: 'payroll-limitation',
        term: 'Payroll limitation',
        category: 'workcomp',
        def: 'State-set minimum and maximum payroll figures applied to officers and certain classes for comp rating, rather than their actual pay.',
        see: ['officer-exclusion', 'rating-basis'],
    },
    {
        id: 'uninsured-subcontractor',
        term: 'Uninsured subcontractor',
        category: 'workcomp',
        essential: true,
        def: 'A sub without their own comp coverage. Their payroll is charged to the hiring contractor at audit, at the sub\'s class rate.',
        detail: 'This is the single largest audit surprise in contracting. Collecting a valid certificate from every sub, valid for the whole period of the work, is the only defence — and the audit will ask for them, not for a list.',
        see: ['audit', 'coi', 'workers-comp'],
    },
    {
        id: 'peo',
        term: 'Professional employer organisation',
        short: 'PEO',
        category: 'workcomp',
        def: 'A firm that becomes the co-employer of a client\'s workforce, providing payroll, HR and workers\' comp under its own master policy.',
        detail: 'Attractive to small employers, but the client builds no experience mod of their own — so leaving a PEO after five years means re-entering the market with no track record and often a poor rate.',
        see: ['experience-mod', 'workers-comp'],
    },
    {
        id: 'pay-as-you-go',
        term: 'Pay as you go',
        category: 'workcomp',
        def: 'Comp premium calculated and paid each payroll run from actual wages, instead of a deposit and a year-end audit.',
        detail: 'Removes the audit shock and helps cash flow. It requires a payroll integration, and a mid-year switch still leaves the old policy to be audited.',
        see: ['audit', 'deposit-premium'],
    },
    {
        id: 'usl-h',
        term: 'USL&H',
        aka: ['longshore', 'longshore and harbor workers'],
        category: 'workcomp',
        def: 'Federal workers\' compensation for maritime workers on or near navigable waters — dock workers, ship repairers, harbour construction.',
        detail: 'Benefits are considerably higher than state comp and it must be endorsed on. Anyone working on a pier, a dock or a vessel under repair needs it checked.',
        see: ['jones-act', 'workers-comp', 'defense-base-act'],
    },
    {
        id: 'jones-act',
        term: 'Jones Act',
        category: 'workcomp',
        def: 'Federal law letting a seaman — a crew member of a vessel — sue their employer for negligence, rather than being limited to comp benefits.',
        detail: 'Covered under protection and indemnity or maritime employers liability, not under state comp. Whether someone is a "seaman" is heavily litigated and turns on their connection to a vessel in navigation.',
        see: ['usl-h', 'ocean-marine', 'exclusive-remedy'],
    },
    {
        id: 'defense-base-act',
        term: 'Defense Base Act',
        short: 'DBA',
        category: 'workcomp',
        def: 'Federal comp for civilian employees working on US military bases or under government contracts overseas.',
        see: ['usl-h', 'workers-comp'],
    },
    {
        id: 'occupational-disease',
        term: 'Occupational disease',
        category: 'workcomp',
        def: 'An illness caused by conditions of employment over time — hearing loss, respiratory disease, repetitive strain — rather than by a single accident.',
        detail: 'The date of injury is often the date of diagnosis or last exposure, which decides which policy year responds, sometimes decades later.',
        see: ['workers-comp', 'occurrence-form'],
    },
    {
        id: 'second-injury-fund',
        term: 'Second injury fund',
        category: 'workcomp',
        def: 'A state fund that reimburses a carrier when a pre-existing condition makes a new work injury much worse, so employers are not discouraged from hiring.',
        see: ['workers-comp'],
    },
    {
        id: 'extraterritorial',
        term: 'Extraterritorial coverage',
        category: 'workcomp',
        def: 'Whether a state\'s comp law follows an employee temporarily working in another state, and for how long.',
        detail: 'Reciprocity rules vary and some states have none. A crew sent across a state line for a two-week job is the everyday version of this problem.',
        see: ['other-states-coverage', 'workers-comp'],
    },
    // -------------------------------------------------------------------- auto
    {
        id: 'covered-auto-symbols',
        term: 'Covered auto symbols',
        aka: ['symbols', 'symbol 1', 'symbol 7', 'symbol 8', 'symbol 9'],
        category: 'auto',
        essential: true,
        def: 'The numbers on a business auto dec page that decide which vehicles each coverage applies to. They matter more than the vehicle schedule.',
        detail: 'Symbol 1 is any auto — the broadest, and what liability should carry. Symbol 2 is owned autos only. Symbol 7 is specifically described autos, which means a newly bought vehicle is uninsured until reported. Symbol 8 is hired autos and 9 is non-owned. Physical damage cannot use symbol 1 and typically uses 7.',
        see: ['hired-auto', 'non-owned-auto', 'commercial-auto', 'newly-acquired-auto'],
    },
    {
        id: 'hired-auto',
        term: 'Hired auto',
        category: 'auto',
        essential: true,
        def: 'Vehicles the business rents, leases, hires or borrows. Symbol 8 covers liability arising from them.',
        detail: 'Hired auto physical damage is separate and often forgotten — it is what pays for damage to the rental car itself, and its absence is why insureds end up buying the rental counter\'s waiver.',
        see: ['covered-auto-symbols', 'non-owned-auto', 'physical-damage'],
    },
    {
        id: 'non-owned-auto',
        term: 'Non-owned auto',
        category: 'auto',
        essential: true,
        def: 'Employees\' own vehicles used for company business. Symbol 9 covers the company\'s liability when an employee crashes their own car on an errand for work.',
        detail: 'It is excess over the employee\'s personal policy and protects the business, not the employee. Any business whose staff drive at all — even to the bank — has this exposure, and a business with no owned vehicles still needs hired and non-owned coverage.',
        see: ['hired-auto', 'covered-auto-symbols', 'employer-non-ownership'],
    },
    {
        id: 'employer-non-ownership',
        term: 'Employers non-ownership liability',
        short: 'ENOL',
        category: 'auto',
        def: 'A standalone version of non-owned auto liability for a business with no vehicles of its own.',
        see: ['non-owned-auto', 'hired-auto'],
    },
    {
        id: 'newly-acquired-auto',
        term: 'Newly acquired auto',
        category: 'auto',
        def: 'An automatic extension covering a vehicle bought mid-term, usually for 30 days or until reported.',
        detail: 'Under symbol 7 the grace period is all that stands between the purchase and no coverage. Under symbol 1 for liability, it is covered from the moment it is acquired.',
        see: ['covered-auto-symbols', 'physical-damage'],
    },
    {
        id: 'physical-damage',
        term: 'Physical damage',
        aka: ['comp and collision', 'comprehensive', 'collision', 'other than collision'],
        category: 'auto',
        def: 'Coverage for damage to the insured\'s own vehicles. Collision is impact and rollover; comprehensive — properly "other than collision" — is theft, fire, glass, hail and animals.',
        see: ['stated-amount', 'covered-auto-symbols', 'acv'],
    },
    {
        id: 'stated-amount',
        term: 'Stated amount',
        category: 'auto',
        def: 'Physical damage written at the lesser of the stated value, actual cash value, or cost to repair. It caps the payout; it does not guarantee it.',
        confused: 'Not agreed value. A stated amount of $80,000 on a truck worth $50,000 still pays $50,000.',
        see: ['physical-damage', 'acv', 'agreed-value'],
    },
    {
        id: 'um-uim',
        term: 'Uninsured / underinsured motorist',
        short: 'UM/UIM',
        category: 'auto',
        essential: true,
        def: 'Covers the insured\'s own people when they are hurt by a driver with no insurance or not enough.',
        detail: 'Selection and rejection rules are state law and strict. An unsigned or defective rejection form frequently means the coverage is deemed to exist at full limits — which cuts both ways and is a common agents E&O claim.',
        see: ['med-pay', 'pip', 'um-rejection'],
    },
    {
        id: 'um-rejection',
        term: 'UM rejection form',
        category: 'documents',
        def: 'The state-prescribed form recording an insured\'s election of lower UM limits or none at all.',
        detail: 'Must be signed by the named insured, on the right form, at the right time. Keep it for the life of the account: courts have reformed policies to full limits over a form that was missing or out of date.',
        see: ['um-uim', 'agents-eo', 'declination-letter'],
    },
    {
        id: 'med-pay',
        term: 'Medical payments',
        category: 'auto',
        def: 'Pays medical bills for the insured\'s occupants after an accident, regardless of fault, up to a small limit.',
        see: ['pip', 'um-uim'],
    },
    {
        id: 'pip',
        term: 'Personal injury protection',
        short: 'PIP',
        aka: ['no-fault'],
        category: 'auto',
        def: 'No-fault coverage for medical costs and lost wages after an auto accident, required in no-fault states.',
        see: ['med-pay', 'um-uim'],
    },
    {
        id: 'drive-other-car',
        term: 'Drive other car',
        short: 'DOC',
        category: 'auto',
        def: 'An endorsement covering an executive who has a company car and no personal auto policy, when they drive a vehicle the company does not own.',
        detail: 'Without it, an owner with only a company car has no coverage in a rental on holiday — the business auto policy covers the entity, not them personally.',
        see: ['non-owned-auto', 'hired-auto'],
    },
    {
        id: 'mcs-90',
        term: 'MCS-90',
        category: 'auto',
        def: 'A federal endorsement on a motor carrier\'s liability policy guaranteeing payment to the public up to statutory limits, even if the policy would not otherwise respond.',
        detail: 'It is a surety-like public protection, not coverage for the insured: the carrier pays and then seeks reimbursement from the trucker. Required for interstate carriers of regulated commodities.',
        see: ['fmcsa', 'radius', 'commercial-auto'],
    },
    {
        id: 'fmcsa',
        term: 'FMCSA filing',
        aka: ['form e', 'form h', 'bmc-91'],
        category: 'auto',
        def: 'Proof of financial responsibility filed by the insurer with federal or state authorities on behalf of a motor carrier.',
        detail: 'Filings must be cancelled formally as well as made — a filing left in place after a policy cancels leaves the carrier exposed to the public.',
        see: ['mcs-90', 'dot-number'],
    },
    {
        id: 'dot-number',
        term: 'DOT number',
        category: 'auto',
        def: 'The federal identifier for a commercial motor carrier, tied to its safety record and inspection history.',
        see: ['csa-score', 'fmcsa'],
    },
    {
        id: 'csa-score',
        term: 'CSA score',
        aka: ['basic scores', 'safer'],
        category: 'auto',
        def: 'Federal safety scores for motor carriers across seven categories, built from roadside inspections and crashes.',
        detail: 'Underwriters look at these before they look at the loss runs. Poor scores in unsafe driving or hours of service will get a trucking submission declined without a quote.',
        see: ['dot-number', 'mvr', 'appetite'],
    },
    {
        id: 'radius',
        term: 'Radius of operation',
        category: 'auto',
        def: 'How far vehicles travel from their home base — local, intermediate, or long haul. It is a primary auto rating factor.',
        detail: 'Misreporting it is a rating error the audit finds. It also drives appetite: many carriers will not write beyond a stated radius at all.',
        see: ['rating-basis', 'appetite', 'mcs-90'],
    },
    {
        id: 'mvr',
        term: 'Motor vehicle record',
        short: 'MVR',
        category: 'auto',
        def: 'A driver\'s licensing and violation history, pulled by the carrier for each listed driver.',
        detail: 'A written driver-qualification standard — MVR pulled at hire and annually, defined unacceptable violations — is one of the easier schedule-rating credits to earn on a fleet.',
        see: ['csa-score', 'schedule-rating', 'driver-list'],
    },
    {
        id: 'driver-list',
        term: 'Driver schedule',
        category: 'auto',
        def: 'The list of drivers reported to the carrier, with licence numbers, dates of birth and years of experience.',
        detail: 'On most business auto policies coverage is not restricted to listed drivers, but an unreported driver with a poor record is a rating problem at audit and a credibility problem at renewal.',
        see: ['mvr', 'covered-auto-symbols'],
    },
    {
        id: 'uiia',
        term: 'UIIA',
        category: 'auto',
        def: 'The Uniform Intermodal Interchange Agreement — the standard contract for truckers pulling ocean containers and chassis, with its own insurance requirements.',
        see: ['trailer-interchange', 'cargo'],
    },
    {
        id: 'dealers-open-lot',
        term: 'Dealers open lot',
        category: 'auto',
        def: 'Physical damage coverage for a dealer\'s inventory of vehicles held for sale, usually written on a reporting or blanket basis.',
        see: ['garage-liability', 'physical-damage'],
    },
];
